import {MISC_KEYS} from "@/api/miscApi";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {resetAllStores} from "@/store/createStore";
import {
  brandImpersonationAtom,
  hasClosedPaymentFailedModalAtom,
  lastSelectedGroupIdAtom,
  latestActiveCampaignForInviteAtom,
  locationAtom,
  selectedCompanyIdAtom,
  tiktokCountryAddSessionAtom,
  tiktokCountrySwitchSessionAtom,
} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {getDefaultStore} from "jotai";
import Cookies from "js-cookie";
import {jwtDecode} from "jwt-decode";
import {create} from "zustand";
import {devtools, persist} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export const TOKEN_STORE_KEY = "token-store";

export const resetUserScopedState = () => {
  const store = getDefaultStore();

  queryClient.clear();
  useUserStore.getState().reset();
  resetAllStores();
  store.set(hasClosedPaymentFailedModalAtom, false);
  store.set(latestActiveCampaignForInviteAtom, null);
  store.set(selectedCompanyIdAtom, null);
  store.set(lastSelectedGroupIdAtom, null);
  store.set(locationAtom, {previousRoute: "", currentRoute: ""});
  store.set(tiktokCountrySwitchSessionAtom, null);
  store.set(tiktokCountryAddSessionAtom, null);
};

type TState = {
  access: string | null;
  refresh: string | null;
  userId: number | null;
  setAccessToken: (token: string | null) => void;
  setRefreshToken: (token: string | null) => void;
  logout(): Promise<void>;
  reset(): void;
};

const initialState = {
  access: null,
  refresh: null,
  userId: null,
};

export const useTokenStore = create<TState>()(
  persist(
    immer(
      devtools(
        (set, get) => ({
          ...initialState,
          setAccessToken: accessToken => {
            const decodedToken = jwtDecode<TokenClaimsSerializer>(accessToken ?? "");
            set(
              state => {
                state.access = accessToken;
                if (
                  decodedToken &&
                  typeof decodedToken === "object" &&
                  "user_id" in decodedToken &&
                  decodedToken.user_id &&
                  typeof decodedToken.user_id === "number"
                )
                  state.userId = decodedToken.user_id;
              },
              false,
              "useTokenStore/setAccessToken",
            );
          },
          setRefreshToken: refreshToken =>
            set(
              state => {
                state.refresh = refreshToken;
              },
              false,
              "useTokenStore/setRefreshToken",
            ),
          logout: async () => {
            if (!get().access) return;

            try {
              await client.post(
                MISC_KEYS.mTokenLogout[0],
                {access: get().access},
                {
                  headers: {
                    Authorization: `Bearer ${get().access}`,
                  },
                },
              );
            } catch (error: any) {
              console.log("Error during logout\n", error.response?.data?.detail);
              window.location.href = `${window.location.origin}/login/`;
            }

            get().reset();
            Cookies.remove("isLoggedIn");
            resetUserScopedState();
            getDefaultStore().set(brandImpersonationAtom, null);
          },
          reset: () => {
            set(() => ({...initialState}), false, "useTokenStore/reset");
            localStorage.removeItem(TOKEN_STORE_KEY);
          },
        }),
        {name: "useTokenStore"},
      ),
    ),
    {name: TOKEN_STORE_KEY},
  ),
);

const syncTokensFromStorage = () => {
  const tokenStore = useTokenStore.getState();
  const stored = localStorage.getItem(TOKEN_STORE_KEY);
  if (!stored) return;

  try {
    const newValue = JSON.parse(stored)?.state;
    if (!newValue) return;

    if (newValue.access && newValue.access !== tokenStore.access) {
      tokenStore.setAccessToken(newValue.access);
      tokenStore.setRefreshToken(newValue.refresh);
    }
  } catch (err) {
    console.error("Failed to sync tokens on focus:", err);
  }
};

window.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    syncTokensFromStorage();
  }
});

export default useTokenStore;
